// src/hooks/useAuth.ts
import { useEffect, useState } from "react";
import { firebaseAuthService } from "../services/firebase-auth.service";
import { useAppStore } from "../store/app.store";
import toast from "react-hot-toast";
import { tiktokPixelService } from "../services/tiktok-pixel.service";

// Custom toast styles for auth messages
const authToastStyles = {
  background: "#000000",
  color: "#ffffff",
  border: "1px solid #333333",
  borderRadius: "16px",
  padding: "12px 16px",
  fontSize: "14px",
  fontWeight: "500",
};

export function useAuth() {
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useAppStore();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = await firebaseAuthService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const unsubscribe = firebaseAuthService.onAuthStateChange(async (user) => {
      setUser(user);

      if (user) {
        void tiktokPixelService.identify({
          email: user.email,
          external_id: user.id,
        });
      }
    });

    return () => unsubscribe();
  }, [setUser]);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      const { user, error } = await firebaseAuthService.signInWithGoogle();

      if (error) {
        if (error.code === "auth/popup-blocked") {
          await firebaseAuthService.signInWithGoogleRedirect();
          toast("Opening sign-in window...", {
            icon: "🪟",
            duration: 3000,
            style: authToastStyles,
          });
          return false;
        }
        toast.error(error.message, {
          style: authToastStyles,
        });
        return false;
      }

      if (user) {
        await tiktokPixelService.identify({
          email: user.email,
          external_id: user.id,
        });

        tiktokPixelService.trackCompleteRegistration(
          {
            content_id: user.id,
            content_type: "product",
            content_name: "lrnsy_registration",
          },
          0,
          "KES",
          `complete_registration:${user.id}`
        );

        toast(`Welcome, ${user.name.split(" ")[0] || "to Lrnsy"}!`, {
          duration: 4000,
          style: authToastStyles,
        });
        return true;
      }

      return false;
    } catch (error) {
      toast.error("Failed to sign in with Google", {
        style: authToastStyles,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await firebaseAuthService.signOut();
      setUser(null);
      toast("👋 Signed out successfully", {
        style: authToastStyles,
      });
    } catch (error) {
      toast.error("Failed to sign out", {
        style: authToastStyles,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    isAuthenticated: !!user,
    signInWithGoogle,
    signOut,
  };
}

export default useAuth;
